import React from "react";
import { ArrowRight, Bookmark, Check, Clock, Globe, MapPin, Phone } from "lucide-react";
import { useCart } from "../../contexts/CartContext";
import { useOrg } from "../../contexts/OrganizationContext";
import { apiService } from "../../services/index";
import { searchService } from "../../services/searchService";
import { parseLatLng, normalizeWebsite, haversine, isValidPhone } from "../../utils/geo";
import SendReferral from "./Detail/SendReferral";
import ShareResult from "./Detail/ShareResult";
import AddToList from "./AddToList";
import CreateAccountCta from "./CreateAccountCta";

const MAX_DESCRIPTION_LENGTH = 250;
const MAX_HOURS_LENGTH = 50;

function truncate(text, max) {
  if (!text) return "";
  const clean = text.replace(/<[^>]+?>/gm, "");
  if (clean.length <= max) return clean;
  return clean.slice(0, max - 1) + "...";
}

function SearchResultCard({ result, searchCoords, isSelected, onSelect, onMouseEnter, onMouseLeave, responseId, surveyName, savedResourceIds, onResourceSaved }) {
  const { contains, addItem, removeItem, itemCount } = useCart();
  const { organization } = useOrg();

  const [showAccountCta, setShowAccountCta] = React.useState(false);

  const inCart = contains(result);
  const isScreenerContext = !!responseId;
  const isResourceSaved = savedResourceIds && savedResourceIds.has(result.program_site_id);
  const latLng = result.latLng || parseLatLng(result.location);

  const addressLine = [result.address_1, result.address_2, result.city, result.state]
    .filter(Boolean)
    .join(", ");

  // Phone priority matches Marionette's search_result model:
  // program_site_phones > program_phones > site_phones > agency_phones
  const rawPhone = result.program_site_phones?.phone_1
    || result.program_site_phones?.phone_2
    || result.program_phones?.phone_1
    || result.site_phones?.phone_1
    || result.agency_phones?.phone_1
    || null;
  const phone = isValidPhone(rawPhone) ? rawPhone : null;
  const website = normalizeWebsite(result.program_site_website || result.program_website || result.site_website || result.agency_website || result.website || "");

  const distance = searchCoords && latLng
    ? haversine(searchCoords, latLng, { unit: "mile" })
    : null;

  // Show referral button if program site has referral emails and referrals feature is enabled
  const referralEmails = typeof result.referral_emails === "string"
    ? (() => { try { return JSON.parse(result.referral_emails); } catch { return result.referral_emails; } })()
    : result.referral_emails;
  const hasReferralEmails = Array.isArray(referralEmails) ? referralEmails.length > 0 : !!referralEmails;
  const showReferral = hasReferralEmails && (organization.userFeatures?.referrals || window.UW211?.ORGANIZATION?.userFeatures?.referrals);

  function handleCartToggle(e) {
    e.stopPropagation();
    if (inCart) {
      removeItem(result);
    } else {
      if (itemCount === 0) setShowAccountCta(true);
      addItem(result);
    }
  }

  function handleSaveResource(e) {
    e.stopPropagation();
    if (isResourceSaved) return;
    const rid = responseId;
    apiService.post(`/response/${rid}/resource_list_items`, { program_site_id: result.program_site_id })
      .then((res) => {
        if (res.hashid && !sessionStorage.getItem("resource_list_id")) {
          sessionStorage.setItem("resource_list_id", res.hashid);
        }
        if (onResourceSaved) onResourceSaved(result.program_site_id);
      });
  }

  const organizationName = [result.agency_name, result.site_name].filter(Boolean).join(" - ");

  return (
    <div
      className={`search-result card-highlight${isSelected ? " selected" : ""}`}
      onMouseEnter={() => onMouseEnter && onMouseEnter(result)}
      onMouseLeave={() => onMouseLeave && onMouseLeave(result)}
    >
      <div className="search-result-card-header">
        <div className="resource-header">
          <h3>
            <a
              role="button"
              tabIndex={0}
              className="name resource-name pointer"
              onClick={() => onSelect && onSelect(result)}
              onKeyDown={(e) => e.key === "Enter" && onSelect && onSelect(result)}
            >
              {result.name || result.agency_name}
            </a>
          </h3>
          {organizationName && (
            <p className="agency-info">at {organizationName}</p>
          )}
        </div>
      </div>

      <div className="search-result-card-meta">
        {distance != null && (
          <div className="search-result-meta-line search-result-distance">
            <span className="distance">{distance.toFixed(1)} miles away</span>
          </div>
        )}

        {addressLine && (
          <div className="resource-location">
            {latLng ? (
              <a
                className="directions small-label"
                href={`https://www.google.com/maps/dir/?api=1&destination=${latLng.lat},${latLng.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => searchService.trackActivity({ action: "directions", name: result.name, site_name: result.site_name, program_at_site_number: result.program_site_id })}
              >
                <MapPin aria-hidden="true" size={16} strokeWidth={1.8} />
                <address className="address resource-address">{addressLine}</address>
              </a>
            ) : (
              <address className="address resource-address">{addressLine}</address>
            )}
          </div>
        )}

        {phone && (
          <div className="search-result-meta-line">
            <a
              className="phone search-result-phone"
              href={`tel:${phone.replace(/\D/g, "")}`}
              onClick={() => searchService.trackActivity({ action: "phone", name: result.name, site_name: result.site_name, program_at_site_number: result.program_site_id })}
            >
              <Phone aria-hidden="true" size={16} strokeWidth={1.8} />
              <span>{phone}</span>
            </a>
          </div>
        )}

        {(result.hours_of_operation || result.hours) && (
          <div className="search-result-meta-line resource-hours">
            <Clock aria-hidden="true" size={16} strokeWidth={1.8} />
            <span className="hours">{truncate(result.hours_of_operation || result.hours, MAX_HOURS_LENGTH)}</span>
          </div>
        )}
      </div>

      {result.description && (
        <p className="description resource-description">
          {truncate(result.description, MAX_DESCRIPTION_LENGTH)}
        </p>
      )}

      {showAccountCta && (
        <div className="create-account-cta-region">
          <CreateAccountCta onDismiss={() => setShowAccountCta(false)} />
        </div>
      )}

      <div className="search-result-card-footer">
        {(website || showReferral) && (
          <div className="resource-information actions search-result-links">
          {website && (
            <a className="information-item small-label website" href={website} target="_blank" rel="noopener noreferrer" onClick={() => searchService.trackActivity({ action: "website", name: result.name, site_name: result.site_name, program_at_site_number: result.program_site_id })} aria-label="Website" title="Website">
              <Globe aria-hidden="true" size={17} strokeWidth={1.8} />
              <span className="information visually-hidden">Website</span>
            </a>
          )}
          {showReferral && (
            <span className="information-item small-label send-referral-region" onClick={(e) => e.stopPropagation()}>
              <SendReferral result={result} isDetailPage={false} />
            </span>
          )}
          </div>
        )}
        <div className="actions search-result-actions">
          {isScreenerContext && (
            <div className="add-to-response-region">
              <div
                className={`result-action small-label save-resource-container${isResourceSaved ? "" : " pointer add-to-response"}`}
                onClick={isResourceSaved ? undefined : handleSaveResource}
                role="button"
                tabIndex={0}
                aria-label={isResourceSaved ? "Saved" : "Save Resource"}
                title={isResourceSaved ? "Saved" : "Save Resource"}
              >
                {isResourceSaved ? (
                  <>
                    <Check aria-hidden="true" size={17} strokeWidth={1.8} />
                    <span className="visually-hidden">Saved</span>
                  </>
                ) : (
                  <>
                    <Bookmark aria-hidden="true" size={17} strokeWidth={1.8} />
                    <span className="visually-hidden">Save Resource</span>
                  </>
                )}
              </div>
            </div>
          )}
          {!window.UW211?.CURRENT_USER && (
            <span className="cart-link-container">
              <span
                className="cart small-label pointer"
                onClick={handleCartToggle}
                role="button"
                tabIndex={0}
                aria-label={inCart ? "Remove from List" : "Add to List"}
                title={inCart ? "Remove from List" : "Add to List"}
              >
                {inCart ? <Check aria-hidden="true" size={17} strokeWidth={1.8} /> : <Bookmark aria-hidden="true" size={17} strokeWidth={1.8} />}
                <span className="visually-hidden">{inCart ? "Remove from List" : "Add to List"}</span>
              </span>
            </span>
          )}
          <div className="add-to-list-region">
            {window.UW211?.CURRENT_USER && <AddToList result={result} compact />}
          </div>
          <div className="share-result-region">
            <ShareResult result={result} compact />
          </div>
        </div>
        <a
          id="more-details"
          className="btn btn-primary more pointer"
          onClick={() => onSelect && onSelect(result)}
          onKeyDown={(e) => e.key === "Enter" && onSelect && onSelect(result)}
          role="button"
          tabIndex={0}
        >
          <span>Details</span>
          <ArrowRight aria-hidden="true" size={17} strokeWidth={1.8} />
        </a>
      </div>
    </div>
  );
}

export default SearchResultCard;
